import { SystemStyleObject } from '@chakra-ui/react';

export const entityWorkspaceStyles: Record<string, SystemStyleObject> = {
  header: {
    bg: 'entityDirectory.surface',
    border: '1px solid',
    borderColor: 'entityDirectory.border',
    color: 'entityDirectory.text',
    flexDirection: 'column',
  },
  headerContent: {
    alignItems: { base: 'stretch', md: 'center' },
    flexDirection: { base: 'column', md: 'row' },
    gap: 4,
    justifyContent: 'space-between',
    px: { base: 4, lg: 6 },
    py: 5,
  },
  identity: {
    alignItems: 'center',
    gap: 3,
    minW: 0,
  },
  initials: {
    alignItems: 'center',
    bg: 'entityDirectory.primaryBackground',
    color: 'entityDirectory.primaryText',
    flexShrink: 0,
    fontFamily: 'instrumentSans',
    fontSize: 'sm',
    fontWeight: 'bold',
    h: '52px',
    justifyContent: 'center',
    w: '52px',
  },
  identityText: {
    flexDirection: 'column',
    gap: 1,
    minW: 0,
  },
  titleRow: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
  },
  title: {
    color: 'entityDirectory.text',
    fontFamily: 'barlow',
    fontSize: { base: '2xl', lg: '32px' },
    fontWeight: 'bold',
    letterSpacing: '-0.02em',
    lineHeight: 1,
  },
  metadata: {
    color: 'entityDirectory.mutedText',
    fontFamily: 'instrumentSans',
    fontSize: '12px',
  },
  actions: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
  },
  navigation: {
    borderTop: '1px solid',
    borderColor: 'entityDirectory.border',
    display: 'flex',
    gap: 6,
    overflowX: 'auto',
    px: { base: 4, lg: 6 },
  },
  navigationItem: {
    alignItems: 'center',
    color: 'entityDirectory.mutedText',
    display: 'flex',
    flexShrink: 0,
    fontFamily: 'instrumentSans',
    fontSize: '13px',
    fontWeight: 'medium',
    gap: 1.5,
    h: '48px',
    position: 'relative',
    textDecoration: 'none',
    _hover: {
      color: 'entityDirectory.text',
      textDecoration: 'none',
    },
  },
  navigationItemActive: {
    color: 'entityDirectory.text',
    fontWeight: 'semibold',
    _after: {
      bg: 'entityDirectory.primaryBackground',
      bottom: 0,
      content: '""',
      h: '3px',
      left: 0,
      position: 'absolute',
      right: 0,
    },
  },
  navigationCount: {
    alignItems: 'center',
    bg: 'entityDirectory.badgeBackground',
    color: 'entityDirectory.mutedText',
    display: 'inline-flex',
    fontSize: '11px',
    fontWeight: 'semibold',
    justifyContent: 'center',
    minW: 5,
    px: 1.5,
    py: 0.5,
  },
  loading: {
    px: { base: 4, lg: 6 },
    py: 5,
  },
  branchPage: {
    color: 'entityDirectory.text',
    width: '100%',
  },
  branchOverview: {
    alignItems: 'flex-start',
    flexDirection: { base: 'column', lg: 'row' },
    gap: 6,
    width: '100%',
  },
  branchSelector: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    maxH: { lg: 'calc(100vh - 230px)' },
    overflowY: { lg: 'auto' },
    pr: { lg: 1 },
    width: { base: '100%', lg: '248px' },
  },
  branchSelectorTitle: {
    color: 'entityDirectory.text',
    fontFamily: 'barlow',
    fontSize: '22px',
    fontWeight: 'bold',
    lineHeight: 1.2,
    mb: 2,
  },
  branchGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  branchGroupSeparated: {
    borderColor: 'entityDirectory.border',
    borderTop: '1px solid',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    mt: 3,
    pt: 3,
  },
  branchGroupLabel: {
    color: 'entityDirectory.mutedText',
    fontFamily: 'instrumentSans',
    fontSize: '11px',
    fontWeight: 'medium',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
  branchItems: {
    border: '1px solid',
    borderColor: 'entityDirectory.border',
    flexDirection: 'column',
  },
  branchItem: {
    alignItems: 'flex-start',
    appearance: 'none',
    borderBottom: '1px solid',
    borderBottomColor: 'entityDirectory.border',
    borderLeft: '3px solid',
    borderLeftColor: 'transparent',
    borderRadius: 0,
    color: 'entityDirectory.text',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    height: 'auto',
    justifyContent: 'center',
    minHeight: '50px',
    px: 3,
    py: 2,
    textAlign: 'left',
    whiteSpace: 'normal',
    width: '100%',
    _focusVisible: {
      outline: '2px solid',
      outlineColor: 'entityDirectory.primaryBackground',
      outlineOffset: '-2px',
    },
    _last: {
      borderBottom: 0,
    },
  },
  branchItemActive: {
    bg: 'entityDirectory.surface',
    _hover: {
      bg: 'entityDirectory.raisedSurface',
    },
  },
  branchItemSelected: {
    bg: 'entityDirectory.selectionBackground',
    borderLeftColor: 'entityDirectory.primaryBackground',
    _hover: {
      bg: 'entityDirectory.selectionBackground',
    },
  },
  branchItemInactive: {
    bg: 'entityDirectory.raisedSurface',
    color: 'entityDirectory.mutedText',
    opacity: 0.65,
    _hover: {
      opacity: 0.8,
    },
  },
  branchItemName: {
    fontFamily: 'instrumentSans',
    fontSize: '13px',
    fontWeight: 'semibold',
    lineHeight: 1.3,
  },
  branchItemNumber: {
    color: 'inherit',
    fontFamily: 'instrumentSans',
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: 1.2,
  },
  branchDetails: {
    bg: 'entityDirectory.surface',
    border: '1px solid',
    borderColor: 'entityDirectory.border',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 5,
    minW: 0,
    p: 6,
    width: '100%',
  },
  branchDetailsHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  branchDetailsTitle: {
    color: 'entityDirectory.text',
    fontFamily: 'barlow',
    fontSize: '24px',
    fontWeight: 'bold',
    lineHeight: 1.2,
  },
  branchDetailsDescription: {
    color: 'entityDirectory.mutedText',
    fontFamily: 'instrumentSans',
    fontSize: '12px',
    mt: 1,
  },
  branchFields: {
    flexDirection: 'column',
    gap: 4,
  },
  branchFieldRow: {
    alignItems: 'flex-start',
    flexDirection: { base: 'column', md: 'row' },
    gap: 5,
    width: '100%',
  },
  branchField: {
    flex: 1,
    minW: 0,
    width: '100%',
    '.supplier-branch-select__control': {
      bg: 'entityDirectory.inputBackground',
      borderColor: 'entityDirectory.secondaryBorder',
      borderRadius: 0,
      color: 'entityDirectory.text',
      minHeight: '32px',
    },
    '.supplier-branch-select__single-value': {
      color: 'entityDirectory.text',
    },
    '.supplier-branch-select__menu': {
      bg: 'entityDirectory.surface',
      color: 'entityDirectory.text',
    },
    '.supplier-branch-select__option': {
      bg: 'entityDirectory.surface',
      color: 'entityDirectory.text',
    },
    '.supplier-branch-select__option--is-focused': {
      bg: 'entityDirectory.raisedSurface',
      color: 'entityDirectory.text',
    },
    '.supplier-branch-select__option--is-selected': {
      bg: 'entityDirectory.primaryBackground',
      color: 'entityDirectory.primaryText',
    },
  },
  branchFooter: {
    alignItems: { base: 'stretch', md: 'center' },
    borderColor: 'entityDirectory.border',
    borderTop: '1px solid',
    flexDirection: { base: 'column', md: 'row' },
    gap: 4,
    justifyContent: 'space-between',
    pt: 4,
  },
  branchRateControl: {
    flex: 1,
  },
  branchRateContent: {
    alignItems: { base: 'flex-start', md: 'center' },
    flexDirection: { base: 'column', md: 'row' },
    gap: { base: 2, md: 8 },
    justifyContent: 'space-between',
  },
  branchRateSummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  branchRateTitle: {
    color: 'entityDirectory.text',
    fontFamily: 'instrumentSans',
    fontSize: '13px',
    fontWeight: 'semibold',
  },
  branchRateDescription: {
    color: 'entityDirectory.mutedText',
    fontFamily: 'instrumentSans',
    fontSize: '12px',
  },
  branchRateInput: {
    maxW: '220px',
    mt: 1,
  },
  branchRateToggle: {
    color: 'entityDirectory.text',
    flexShrink: 0,
    fontFamily: 'instrumentSans',
    fontSize: '13px',
    fontWeight: 'normal',
    height: 'auto',
    minW: 0,
    textDecoration: 'underline',
  },
  branchFooterSpacer: {
    flex: 1,
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  modalFooter: {
    gap: 2,
  },
};

export function getEntityWorkspaceNavigationItemStyle(
  active: boolean,
): SystemStyleObject {
  return active
    ? {
        ...entityWorkspaceStyles.navigationItem,
        ...entityWorkspaceStyles.navigationItemActive,
      }
    : entityWorkspaceStyles.navigationItem;
}

export function getEntityWorkspaceListItemStyle(
  active: boolean,
  selected: boolean,
): SystemStyleObject {
  return {
    ...entityWorkspaceStyles.branchItem,
    ...(active
      ? entityWorkspaceStyles.branchItemActive
      : entityWorkspaceStyles.branchItemInactive),
    ...(selected ? entityWorkspaceStyles.branchItemSelected : {}),
  };
}

export const articleWorkspaceStyles: Record<string, SystemStyleObject> = {
  page: {
    width: '100%',
    minW: 0,
    boxSizing: 'border-box',
    px: { base: 4, lg: '48px' },
    py: '32px',
    '& > div': { maxW: '1344px', minW: 0, gap: '24px' },
  },
  sectionHeader: {
    alignItems: 'flex-start',
    borderBottom: '1px solid',
    borderColor: 'articleManagement.divider',
    justifyContent: 'space-between',
    px: 5,
    py: '18px',
  },
  sectionTitle: { fontFamily: 'Barlow', fontSize: '19px', lineHeight: '23px' },
  sectionDescription: {
    color: 'articleManagement.mutedText',
    fontSize: '12px',
    lineHeight: '18px',
    mt: '3px',
  },
  numberField: { display: 'flex', flexDirection: 'column', gap: 1 },
  card: {
    bg: 'articleManagement.surface',
    border: '1px solid',
    borderColor: 'articleManagement.border',
  },
  columns: {
    display: 'grid',
    gridTemplateColumns: { base: '1fr', '2xl': 'minmax(0, 1fr) 320px' },
    gap: 6,
    alignItems: 'start',
  },
  fields: {
    display: 'grid',
    gridTemplateColumns: { base: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
    gap: 5,
  },
  tableScroll: { overflowX: 'auto', minW: 0 },
};

export const articleJobWorkspaceStyles: Record<string, SystemStyleObject> = {
  sections: { display: 'flex', flexDirection: 'column', minW: 0, gap: 6 },
  page: {
    ...articleWorkspaceStyles.page,
    minH: '100dvh',
    px: { base: 4, lg: 6, xl: 8 },
    '& > div': { maxW: 'none', minW: 0, gap: '24px' },
  },
  columns: {
    ...articleWorkspaceStyles.columns,
    gridTemplateColumns: {
      base: 'minmax(0, 1fr)',
      lg: 'minmax(0, 1fr) 300px',
      xl: 'minmax(0, 1fr) 336px',
    },
    gap: { base: 6, xl: 8 },
  },
  summary: {
    position: { lg: 'sticky' },
    top: 4,
    maxH: { lg: 'calc(100dvh - 144px)' },
    minW: 0,
    minH: 0,
    display: 'flex',
    flexDirection: 'column',
    bg: 'articleManagement.surface',
    color: 'articleManagement.text',
    fontFamily: 'instrumentSans',
    borderStyle: 'solid',
    borderLeftWidth: { lg: '1px' },
    borderTopWidth: { base: '1px', lg: 0 },
    borderColor: 'articleManagement.border',
    '& p': { m: 0 },
  },
  summaryContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    minH: 0,
    overflowY: { lg: 'auto' },
    overflowWrap: 'anywhere',
    p: { base: 5, xl: '32px 28px' },
  },
  summaryTitle: {
    fontFamily: 'barlow',
    fontSize: '24px',
    fontWeight: 'semibold',
    lineHeight: '30px',
    textTransform: 'none',
  },
  summaryCustomer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  summaryCustomerLink: {
    fontFamily: 'barlow',
    fontSize: '28px',
    fontWeight: 'semibold',
    lineHeight: '34px',
    color: 'articleManagement.accent',
    _hover: { color: 'articleManagement.accentHover' },
  },
  summarySecondary: {
    fontSize: '16px',
    lineHeight: '24px',
    color: 'articleManagement.mutedText',
  },
  summaryGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2.5,
    borderTop: '1px solid',
    borderColor: 'articleManagement.border',
    pt: 6,
  },
  summaryCountRow: { display: 'flex', alignItems: 'baseline', gap: 3 },
  summaryCount: {
    fontFamily: 'barlow',
    fontSize: '48px',
    fontWeight: 'semibold',
    lineHeight: '52px',
  },
  summaryCountLabel: { fontSize: '20px', lineHeight: '28px' },
  summaryCodes: { fontSize: '18px', lineHeight: '26px' },
  summaryDetails: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, auto)',
    gap: '18px 12px',
    borderTop: '1px solid',
    borderColor: 'articleManagement.border',
    pt: 6,
    m: 0,
    fontSize: '18px',
    lineHeight: '26px',
    '& dt, & dd': { m: 0 },
    '& dd': { textAlign: 'right', fontWeight: 'semibold' },
  },
  summaryIncludedDetails: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gap: '12px',
    m: 0,
    fontSize: '16px',
    lineHeight: '24px',
    '& dt, & dd': { m: 0 },
    '& dd': { textAlign: 'right', fontWeight: 'semibold' },
  },
  summaryAlert: { alignItems: 'flex-start', borderRadius: 0, fontSize: 'sm' },
  summaryActions: {
    flexShrink: 0,
    maxH: { lg: '100%' },
    overflowY: { lg: 'auto' },
  },
};

const copyArticleTagStyle: SystemStyleObject = {
  color: 'white',
  fontSize: '10px',
  lineHeight: '16px',
  px: 1.5,
};

const copyArticleBranchStyle: SystemStyleObject = {
  bg: 'articleManagement.subtleSurface',
  border: '1px solid',
  borderColor: 'articleManagement.divider',
  minH: '58px',
  p: 3,
};

const copyArticleBranchTypeStyle: SystemStyleObject = {
  color: 'articleManagement.mutedText',
  fontSize: '9px',
  fontWeight: '700',
  textTransform: 'uppercase',
};

const copyArticleInformationOptionStyle: SystemStyleObject = {
  ...articleWorkspaceStyles.card,
  minH: '82px',
  p: 3,
};

export const copyArticleStyles: Record<string, SystemStyleObject> = {
  cardBody: { px: 5, py: '18px' },
  sourceSelectors: {
    gridTemplateColumns: { base: '1fr', md: 'minmax(280px, 0.45fr) 1fr' },
    gap: 4,
  },
  sourceLoading: { alignItems: 'center', gap: 3, minH: '52px' },
  sourceLoadingText: { color: 'articleManagement.mutedText', fontSize: 'sm' },
  sourceSelected: {
    alignItems: 'center',
    justifyContent: 'space-between',
    bg: 'articleManagement.subtleSurface',
    borderLeft: '3px solid',
    borderLeftColor: 'articleManagement.accent',
    px: '14px',
    py: '13px',
  },
  sourceName: { fontSize: '13px', fontWeight: '700' },
  sourceDetails: {
    color: 'articleManagement.mutedText',
    fontSize: '11px',
  },
  sourceBadge: {
    bg: 'navigation.globalBackground',
    borderRadius: 0,
    color: 'white',
    fontFamily: 'Barlow',
  },
  stepLabel: {
    color: 'articleManagement.mutedText',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '0.09em',
  },
  sectionLabel: { fontSize: '13px', fontWeight: '700', mb: 3 },
  informationGrid: {
    gap: 3,
    gridTemplateColumns: { base: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  },
  informationName: { fontSize: '12px', fontWeight: 'semibold' },
  informationEmail: {
    color: 'articleManagement.mutedText',
    fontSize: '11px',
  },
  informationTags: { gap: 1, flexWrap: 'wrap' },
  informationTag: copyArticleTagStyle,
  informationTagArchived: {
    ...copyArticleTagStyle,
    opacity: 0.55,
    textDecoration: 'line-through',
  },
  informationTier: { borderRadius: 0 },
  informationTierArchived: {
    borderRadius: 0,
    opacity: 0.55,
    textDecoration: 'line-through',
  },
  branchesHeader: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    mb: 3,
  },
  branchesTitle: { fontSize: '13px', fontWeight: '700' },
  branchesDescription: {
    color: 'articleManagement.mutedText',
    fontSize: '11px',
    mt: 1,
  },
  smallMuted: { color: 'articleManagement.mutedText', fontSize: '11px' },
  branchesGrid: {
    gap: 2,
    gridTemplateColumns: { base: '1fr', md: 'repeat(4, minmax(0, 1fr))' },
  },
  branch: copyArticleBranchStyle,
  branchChecked: {
    ...copyArticleBranchStyle,
    borderColor: 'articleManagement.accent',
  },
  branchIdentifier: { fontSize: '12px', fontWeight: '600' },
  branchType: copyArticleBranchTypeStyle,
  branchTypeNewLabel: {
    ...copyArticleBranchTypeStyle,
    color: 'articleManagement.accent',
  },
  informationOption: copyArticleInformationOptionStyle,
  informationOptionChecked: {
    ...copyArticleInformationOptionStyle,
    bg: 'articleManagement.selectedSurface',
    borderColor: 'articleManagement.accent',
  },
  informationOptionCheckbox: { fontSize: '13px', fontWeight: 'semibold' },
  informationOptionValue: { mt: 2, pl: 6 },
  informationOptionInactiveValue: { mt: 2, pl: 6, opacity: 0.65 },
  destinationTitle: { fontSize: '14px', fontWeight: '700' },
  destinationGrid: {
    gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
    gap: 4,
    mt: 4,
  },
  modeControl: { display: 'flex', flexDirection: 'column', gap: 2 },
  fieldsGrid: { gridTemplateColumns: { base: '1fr', md: '1fr 1fr' }, gap: 4 },
  fieldControl: { display: 'flex', flexDirection: 'column', gap: 1 },
  rangeGrid: {
    gridTemplateColumns: { base: '1fr 1fr', md: '1.3fr 1fr 1fr 1fr' },
    gap: 4,
  },
  preview: { border: '1px solid', borderColor: 'articleManagement.divider' },
  previewHeader: {
    bg: 'articleManagement.subtleSurface',
    borderBottom: '1px solid',
    borderColor: 'articleManagement.divider',
    justifyContent: 'space-between',
    px: 3,
    py: 2,
  },
  previewTitle: {
    fontSize: '11px',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  previewList: { maxH: '420px', overflowY: 'auto' },
  previewEmpty: {
    color: 'articleManagement.mutedText',
    fontSize: '12px',
    px: 3,
    py: 4,
  },
  previewRow: {
    alignItems: 'center',
    justifyContent: 'space-between',
    minH: '42px',
    px: 3,
  },
  previewCode: { fontSize: '12px', fontWeight: '700' },
  previewName: { color: 'articleManagement.mutedText', fontSize: '10px' },
  availabilityBadge: { borderRadius: 0 },
};

export const copyArticleSelectStyles = { borderRadius: 0, minHeight: '42px' };

export const createArticleStyles: Record<string, SystemStyleObject> = {
  ...articleJobWorkspaceStyles,

  availableStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    textTransform: 'none',
    fontSize: '13px',
  },
  page: {
    ...articleJobWorkspaceStyles.page,
    '& .create-article-card': {
      minW: 0,
      '& > div:first-of-type': articleWorkspaceStyles.sectionHeader,
      '& > div:last-of-type': { p: 5 },
      '& h2': articleWorkspaceStyles.sectionTitle,
      '& > div:first-of-type p': articleWorkspaceStyles.sectionDescription,
    },
    '& .chakra-form-control': { minW: 0 },
    '& .chakra-form__label': { mb: '8px', lineHeight: '16px' },
    '& .chakra-input, & select': {
      height: '44px',
      borderRadius: 0,
      boxSizing: 'border-box',
    },
    '& .create-article-select__placeholder': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .create-article-select__control': { minHeight: '44px', borderRadius: 0 },
  },
  tableScroll: articleWorkspaceStyles.tableScroll,
  branchTable: {
    tableLayout: 'fixed',
    width: '100%',
    minW: '920px',
    '& thead tr th, & tbody tr td': { px: '16px', verticalAlign: 'middle' },
    '& thead tr th': {
      py: '12px',
      lineHeight: '16px',
      whiteSpace: 'nowrap',
      textTransform: 'none',
    },
    '& tbody tr td': {
      py: '10px',
      height: '61px',
      lineHeight: '18px',
      fontSize: '14px',
    },
    '& th:first-of-type, & td:first-of-type': { width: '60px' },
    '& th:nth-of-type(2)': { width: '240px' },
    '& th:nth-of-type(3)': { width: '100px' },
    '& th:nth-of-type(4)': { width: '100px' },
  },
  numberTable: { '& th, & td': { px: '16px', py: '12px', lineHeight: '18px' } },
  stack: { display: 'flex', flexDirection: 'column', minW: 0, gap: 4 },
  fields: articleWorkspaceStyles.fields,
  row: { display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' },
  muted: { color: 'articleManagement.mutedText', fontSize: 'sm' },
  regionLabel: { m: 0 },
  branchCount: {
    m: 0,
    ml: 'auto',
    color: 'articleManagement.mutedText',
    fontSize: '13px',
  },
  matrixContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minW: 0,
    p: 4,
    bg: 'articleManagement.subtleSurface',
    border: '1px solid',
    borderColor: 'articleManagement.border',
  },
  proposedFields: {
    ...articleWorkspaceStyles.fields,
    '@container (max-width: 600px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  proposedList: { display: 'flex', flexDirection: 'column', gap: 4, minW: 0 },
  proposedTitle: {
    minW: 0,
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    whiteSpace: 'normal',
    textAlign: 'left',
    overflowWrap: 'anywhere',
  },
  proposedSummary: {
    color: 'articleManagement.mutedText',
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '20px',
  },
  proposedCardHeader: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 3,
    p: { base: 4, md: 5 },
  },
  proposedToggle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 3,
    flex: '1 1 200px',
    minW: 0,
    height: 'auto',
    _focusVisible: { boxShadow: 'outline' },
    '& > svg': { flexShrink: 0 },
  },
  proposedCardActions: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 3,
    '& > button': { flexShrink: 0 },
  },
  proposedAvailability: {
    flexBasis: '100%',
    fontSize: '13px',
    pl: 7,
    overflowWrap: 'anywhere',
  },
  proposedReadiness: {
    textTransform: 'none',
    borderRadius: 0,
    fontSize: '12px',
    fontWeight: 500,
    px: 2,
    py: 1,
  },
  fieldWarning: {
    mt: 1,
    fontSize: '13px',
    color: 'orange.700',
    _dark: { color: 'orange.200' },
  },
  proposedContent: {
    minW: 0,
    containerType: 'inline-size',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    p: { base: 4, md: 6 },
    borderTop: '1px solid',
    borderColor: 'articleManagement.border',
    '& .chakra-button': { fontSize: '13px', flexShrink: 0 },
  },
  layoutFile: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 3,
    minH: '40px',
    '& > a:not(:first-of-type)': { fontSize: '13px', flexShrink: 0 },
    '& .chakra-button:not(:last-of-type)': { minW: 0, px: 0 },
  },
  layoutPdf: {
    p: '6px',
    m: 0,
    flexShrink: 0,
    bg: 'entityDirectory.raisedSurface',
    fontSize: '13px',
  },
  layoutFilename: {
    flex: 1,
    minW: 0,
    overflowWrap: 'anywhere',
    fontSize: '14px',
  },
  proposed: {
    minW: 0,
    bg: 'articleManagement.surface',
    border: '1px solid',
    borderColor: 'articleManagement.border',
  },
};

const certificateTag: SystemStyleObject = {
  borderWidth: '1px',
  borderStyle: 'solid',
  borderRadius: 0,
  maxW: '100%',
  minH: '28px',
  height: 'auto',
  py: 1,
  fontSize: '12px',
  textTransform: 'none',
};

export const certificateSelectStyles: Record<string, SystemStyleObject> = {
  selection: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 2,
    minW: 0,
  },
  selectedTags: {
    display: 'flex',
    flex: '1 1 120px',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 2,
    minW: 0,
  },
  picker: {
    flex: '0 0 320px',
    maxW: '100%',
    '& > div': { width: '100%' },
    '& .branch-certificate-select__control': {
      minHeight: '40px',
      borderRadius: 0,
    },
    '& .branch-certificate-select__placeholder': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  scopedTag: {
    ...certificateTag,
    bg: 'entityDirectory.surface',
    color: 'entityDirectory.text',
    borderColor: 'entityDirectory.border',
  },
  transactionalTag: {
    ...certificateTag,
    bg: 'complianceDocument.transactionalBackground',
    color: 'complianceDocument.transactionalText',
    borderColor: 'complianceDocument.transactionalBorder',
  },
  tagLabel: {
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: '16px',
  },
  legend: { alignItems: 'center', flexWrap: 'wrap', gap: 4 },
};

export const articleCertificateStyles: Record<string, SystemStyleObject> = {
  page: {
    '& .article-certificate-card > div:last-of-type': { p: 0 },
  },
  messages: { px: 6, pt: 6 },
  filters: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    px: 6,
    py: 5,
  },
  regionFilter: {
    width: { base: '100%', md: '172px' },
    flexShrink: 0,
    '& select': { height: '38px', borderRadius: 0 },
  },
  footer: {
    ...entityWorkspaceStyles.branchRateContent,
    borderTop: '1px solid',
    borderColor: 'articleManagement.border',
    justifyContent: 'space-between',
    px: 6,
    py: 5,
  },
  table: {
    ...createArticleStyles.branchTable,
    minW: '920px',
    '& th:first-of-type, & td:first-of-type': { width: '240px', pl: 6 },
    '& th:nth-of-type(2)': { width: '100px' },
    '& th:nth-of-type(3)': { width: '100px' },
    '& th:nth-of-type(4)': { width: 'auto' },
    '& th:last-of-type, & td:last-of-type': { pr: 6 },
  },
};

export const compactDropzoneStyles = {
  root: {
    borderWidth: '1px',
    borderRadius: 0,
    bg: 'entityDirectory.raisedSurface',
    textAlign: 'left',
  },
  content: {
    p: '4px 8px',
    position: 'relative',
    '& > input': { position: 'absolute' },
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 3,
  },
  description: {
    m: 0,
    flex: '1 1 140px',
    minW: 0,
    fontSize: '14px',
    lineHeight: '18px',
    color: 'articleManagement.mutedText',
  },
  button: {
    flexShrink: 0,
    borderRadius: 0,
    px: '16px',
    py: '10px',
    height: '30px',
    fontSize: '14px',
  },
};

export const articleJobSubmitStyles: SystemStyleObject = {
  borderRadius: 0,
  color: 'white',
  h: '42px',
  mt: 3,
  width: '100%',
  _disabled: { color: 'white', opacity: 0.6 },
};

export const articleJobActionStyles: Record<string, SystemStyleObject> = {
  submit: articleJobSubmitStyles,
  card: { bg: 'navigation.globalBackground', color: 'white', p: 4 },
  title: {
    color: 'white',
    fontFamily: 'Barlow',
    fontSize: '16px',
    _light: { color: 'white' },
  },
  description: {
    color: 'navigation.globalMuted',
    fontSize: '11px',
    lineHeight: '17px',
    mt: 2,
  },
  options: {
    borderTop: '1px solid',
    borderColor: 'whiteAlpha.300',
    mt: 3,
    pt: 3,
  },
  ignored: {
    color: 'navigation.globalMuted',
    fontSize: '11px',
    lineHeight: '17px',
    mt: 1,
  },
};

export const articleJobSidebarActionStyles: Record<string, SystemStyleObject> =
  {
    card: {
      bg: 'articleManagement.subtleSurface',
      color: 'articleManagement.text',
      borderTop: '1px solid',
      borderColor: 'articleManagement.border',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      p: { base: 5, xl: '24px 28px' },
    },
    title: {
      fontFamily: 'barlow',
      fontSize: '18px',
      lineHeight: '24px',
      textTransform: 'none',
    },
    description: {
      color: 'articleManagement.mutedText',
      fontSize: '14px',
      lineHeight: '21px',
      order: 1,
    },
    options: {
      '& .chakra-form-control': { width: '100%' },
      '& .chakra-form__label': { fontSize: '15px', lineHeight: '22px' },
      '& .chakra-switch': { flexShrink: 0 },
    },
    ignored: {
      color: 'articleManagement.mutedText',
      fontSize: '14px',
      lineHeight: '20px',
      pt: 2,
    },
    submit: {
      ...articleJobSubmitStyles,
      h: '52px',
      mt: 0,
      flexShrink: 0,
      fontFamily: 'barlow',
      fontSize: '20px',
      lineHeight: '26px',
    },
  };
